"use client";

import React from "react";
import Icon from "@/components/library/Icon";
import {CitationSource} from "@/types/ask";
import {InlineCitationCard, InlineCitationCardBody} from "@/components/ai-elements/inline-citation";
import {HoverCardTrigger} from "@/components/ui/hover-card";
import {getDomain} from "@/utils/url";

interface CitationBadgeProps {
   numbers: number[];
   citations: CitationSource[];
}

const CitationBadge: React.FC<CitationBadgeProps> = ({numbers, citations}) => {
   const grouped = numbers
      .flatMap((n) => citations.filter((c) => c.citationNumber === n))
      .filter((c): c is CitationSource => Boolean(c));

   if (grouped.length === 0) return null;

   const source = grouped[0];
   const isWeb = !!source.url;
   const domain = isWeb ? getDomain(source.url!) : null;
   const title = source.title || source.fileName || "Source";
   const hasPages = source.pages && source.pages.length > 0;

   return (
      <InlineCitationCard>
         <HoverCardTrigger asChild>
            {isWeb ? (
               <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 ml-1
                             text-[11px] font-medium leading-none cursor-pointer select-none
                             align-text-top text-primary/70 hover:text-primary transition-colors"
               >
                  <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary/10 text-primary/70 text-[10px] font-medium shrink-0">
                     {numbers[0]}
                  </span>
               </a>
            ) : (
               <span
                  className="inline-flex items-center gap-1 ml-1
                             text-[11px] font-medium leading-none cursor-pointer select-none
                             align-text-top text-primary/70 hover:text-primary transition-colors"
               >
                  <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary/10 text-primary/70 text-[10px] font-medium shrink-0">
                     {numbers[0]}
                  </span>
               </span>
            )}
         </HoverCardTrigger>
         <InlineCitationCardBody
            className="relative w-72 p-0 bg-panel border border-neutral shadow-lg rounded-lg overflow-hidden"
         >
            {isWeb ? (
               <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 hover:bg-100 transition-colors"
                  onClick={(e) => e.stopPropagation()}
               >
                  <div className="flex items-start gap-2">
                     <div className="min-w-0 flex-1 flex flex-col">
                        <span className="text-sm font-medium text-900 leading-snug line-clamp-2">{title}</span>
                        <span className="text-xs text-500 mt-1.5 flex items-center gap-1">
                           <span className="truncate">{domain}</span>
                           <Icon icon="arrow-up-right-from-square" className="w-2.5 h-2.5 shrink-0" />
                        </span>
                     </div>
                  </div>
               </a>
            ) : (
               <div className="p-3">
                  <p className="text-sm font-medium text-900 line-clamp-2 leading-snug">{title}</p>
                  {hasPages && (
                     <p className="text-xs text-500 mt-1">Pages {source.pages!.join(", ")}</p>
                  )}
               </div>
            )}
         </InlineCitationCardBody>
      </InlineCitationCard>
   );
};

export default CitationBadge;
